<template>
  <main class="not-found-page">
    <div class="bg-grid" aria-hidden="true"></div>
    <div class="bg-glow glow-one" aria-hidden="true"></div>
    <div class="bg-glow glow-two" aria-hidden="true"></div>

    <section class="not-found-shell">
      <!-- LEFT COPY -->
      <div class="not-found-copy">
        <RouterLink class="brand" to="/">
          <img
            src="@/assets/bluespeedlogo-transparent.png"
            alt="Blue Speed Construction"
          />
        </RouterLink>

        <p class="eyebrow">
          <span></span>
          Page not found
        </p>

        <h1>404</h1>

        <h2>We couldn’t find this page.</h2>

        <p class="description">
          The page may have been moved, renamed, or the link is no longer active.
          Head back to the main page and continue from there.
        </p>

        <div class="actions">
          <RouterLink class="primary-btn" to="/">
            Back to Home
            <ArrowRight class="btn-icon" />
          </RouterLink>

          <a
            class="secondary-btn"
            :href="whatsappUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp Us
          </a>
        </div>

        <p class="small-note">
          Blue Speed Construction · Rope access · Maintenance · Contractor works
        </p>
      </div>

      <!-- RIGHT ANIMATION -->
      <div class="animation-panel" aria-hidden="true">
        <div class="sun"></div>

        <div class="cloud cloud-one"></div>
        <div class="cloud cloud-two"></div>

        <div class="building building-one">
          <span v-for="n in 18" :key="`b1-${n}`"></span>
        </div>

        <div class="building building-two">
          <span v-for="n in 24" :key="`b2-${n}`"></span>
        </div>

        <div class="building building-three">
          <span v-for="n in 15" :key="`b3-${n}`"></span>
        </div>

        <div class="search-scene">
          <div class="blueprint-card">
            <span>SEARCHING</span>
            <strong>Page location unknown</strong>
            <p>Checking available routes...</p>

            <div class="blueprint-lines">
              <i></i>
              <i></i>
              <i></i>
            </div>
          </div>

          <div class="magnifier-large"></div>

          <div class="search-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <div class="map-pin">
          <span>?</span>
        </div>

        <div class="traffic-cone cone-one">
          <span></span>
        </div>

        <div class="traffic-cone cone-two">
          <span></span>
        </div>

        <div class="warning-card">
          <span>404</span>
          <strong>Search came up empty</strong>
          <p>The page may have moved or no longer exists.</p>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { ArrowRight } from "lucide-vue-next";

import { whatsappLink } from "@/config/company";

export default {
  name: "NotFound",

  components: {
    ArrowRight,
  },

  data() {
    return {
      whatsappUrl: whatsappLink(),
    };
  },
};
</script>

<style scoped>
.not-found-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at 12% 10%, rgba(37, 99, 235, 0.1), transparent 28%),
    radial-gradient(circle at 88% 18%, rgba(220, 38, 38, 0.08), transparent 24%),
    linear-gradient(180deg, #f8fbff 0%, #eef5ff 52%, #ffffff 100%);
  color: #111827;
  display: flex;
  align-items: center;
  padding: 48px 0;
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(15, 23, 42, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15, 23, 42, 0.045) 1px, transparent 1px);
  background-size: 54px 54px;
  mask-image: linear-gradient(to bottom, black, transparent 80%);
  pointer-events: none;
}

.bg-glow {
  position: absolute;
  width: 440px;
  height: 440px;
  border-radius: 999px;
  filter: blur(90px);
  opacity: 0.22;
  pointer-events: none;
}

.glow-one {
  top: -160px;
  left: -160px;
  background: #2563eb;
}

.glow-two {
  right: -140px;
  bottom: -160px;
  background: #dc2626;
}

.not-found-shell {
  position: relative;
  z-index: 2;
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: 0.92fr 1.08fr;
  gap: 48px;
  align-items: center;
}

.not-found-copy {
  position: relative;
  z-index: 3;
}

.brand {
  display: inline-flex;
  align-items: center;
  margin-bottom: 42px;
}

.brand img {
  width: 168px;
  height: auto;
  display: block;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 18px;
  color: #2563eb;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.eyebrow span {
  width: 40px;
  height: 2px;
  background: #dc2626;
}

h1,
h2 {
  margin: 0;
  font-family: "Manrope", "Inter", Arial, sans-serif;
  letter-spacing: -0.06em;
}

h1 {
  font-size: clamp(98px, 15vw, 190px);
  line-height: 0.86;
  font-weight: 900;
  color: #0f172a;
}

h2 {
  max-width: 620px;
  margin-top: 24px;
  font-size: clamp(36px, 5vw, 68px);
  line-height: 1;
  font-weight: 850;
  color: #1e3a8a;
}

.description {
  max-width: 560px;
  margin: 24px 0 0;
  color: #475569;
  font-size: 16px;
  line-height: 1.8;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 34px;
}

.primary-btn,
.secondary-btn {
  min-height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 22px;
  border-radius: 0;
  font-size: 13px;
  font-weight: 800;
  text-decoration: none;
  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    background 180ms ease,
    color 180ms ease;
}

.primary-btn {
  background: #dc2626;
  color: #ffffff;
  box-shadow: 0 14px 34px rgba(220, 38, 38, 0.2);
}

.primary-btn:hover {
  transform: translateY(-2px);
  background: #b91c1c;
  box-shadow: 0 18px 40px rgba(220, 38, 38, 0.28);
}

.secondary-btn {
  border: 1px solid rgba(37, 99, 235, 0.25);
  background: #ffffff;
  color: #1e3a8a;
}

.secondary-btn:hover {
  transform: translateY(-2px);
  border-color: rgba(37, 99, 235, 0.45);
  box-shadow: 0 14px 32px rgba(37, 99, 235, 0.12);
}

.btn-icon {
  width: 17px;
  height: 17px;
}

.small-note {
  margin: 24px 0 0;
  color: #64748b;
  font-size: 12px;
}

/* =========================
   ANIMATION PANEL
========================= */

.animation-panel {
  position: relative;
  min-height: 640px;
  border: 1px solid rgba(37, 99, 235, 0.14);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.78), rgba(239, 246, 255, 0.9)),
    #ffffff;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.1);
  overflow: hidden;
}

.animation-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(15, 23, 42, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15, 23, 42, 0.04) 1px, transparent 1px);
  background-size: 42px 42px;
  pointer-events: none;
}

.sun {
  position: absolute;
  top: 54px;
  right: 62px;
  width: 86px;
  height: 86px;
  border-radius: 999px;
  background: #fbbf24;
  box-shadow: 0 0 60px rgba(251, 191, 36, 0.32);
}

.cloud {
  position: absolute;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  animation: cloudMove 9s ease-in-out infinite alternate;
}

.cloud::before,
.cloud::after {
  content: "";
  position: absolute;
  bottom: 0;
  border-radius: 999px;
  background: inherit;
}

.cloud::before {
  width: 44px;
  height: 44px;
  left: 18px;
}

.cloud::after {
  width: 34px;
  height: 34px;
  right: 20px;
}

.cloud-one {
  top: 86px;
  left: 54px;
  width: 128px;
}

.cloud-two {
  top: 150px;
  right: 156px;
  width: 92px;
  opacity: 0.75;
  animation-delay: 1.2s;
}

@keyframes cloudMove {
  from {
    transform: translateX(-8px);
  }

  to {
    transform: translateX(18px);
  }
}

/* =========================
   BUILDINGS
========================= */

.building {
  position: absolute;
  bottom: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 18px;
  background: #dbeafe;
  border: 1px solid rgba(37, 99, 235, 0.12);
}

.building span {
  height: 18px;
  background: rgba(37, 99, 235, 0.18);
}

.building-one {
  left: 32px;
  width: 138px;
  height: 300px;
  opacity: 0.75;
}

.building-two {
  left: 190px;
  width: 180px;
  height: 440px;
  background: #e0f2fe;
}

.building-three {
  right: 34px;
  width: 150px;
  height: 360px;
  background: #dbeafe;
  opacity: 0.9;
}

/* =========================
   CLEAN SEARCH SCENE
========================= */

.search-scene {
  position: absolute;
  left: 47%;
  top: 155px;
  width: 360px;
  height: 340px;
  transform: translateX(-50%);
}

.blueprint-card {
  position: absolute;
  left: 24px;
  top: 82px;
  width: 250px;
  min-height: 170px;
  padding: 22px;
  background:
    linear-gradient(90deg, rgba(37, 99, 235, 0.08) 1px, transparent 1px),
    linear-gradient(rgba(37, 99, 235, 0.08) 1px, transparent 1px),
    #ffffff;
  background-size: 16px 16px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  box-shadow: 0 22px 50px rgba(15, 23, 42, 0.12);
  transform: rotate(-3deg);
  animation: blueprintFloat 3.8s ease-in-out infinite;
}

@keyframes blueprintFloat {
  0%,
  100% {
    transform: rotate(-3deg) translateY(0);
  }

  50% {
    transform: rotate(-1deg) translateY(-8px);
  }
}

.blueprint-card span {
  display: inline-flex;
  padding: 7px 9px;
  background: rgba(37, 99, 235, 0.09);
  color: #2563eb;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 0.14em;
}

.blueprint-card strong {
  display: block;
  margin-top: 16px;
  color: #0f172a;
  font-size: 22px;
  line-height: 1.15;
  font-family: "Manrope", "Inter", Arial, sans-serif;
}

.blueprint-card p {
  margin: 10px 0 0;
  color: #64748b;
  font-size: 13px;
}

.blueprint-lines {
  display: grid;
  gap: 8px;
  margin-top: 22px;
}

.blueprint-lines i {
  display: block;
  height: 7px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.16);
}

.blueprint-lines i:nth-child(1) {
  width: 82%;
}

.blueprint-lines i:nth-child(2) {
  width: 60%;
}

.blueprint-lines i:nth-child(3) {
  width: 72%;
}

.magnifier-large {
  position: absolute;
  right: 12px;
  top: 36px;
  width: 128px;
  height: 128px;
  border: 12px solid #1e3a8a;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  box-shadow: 0 24px 52px rgba(30, 58, 138, 0.18);
  animation: magnifierSearch 2.4s ease-in-out infinite;
}

.magnifier-large::before {
  content: "";
  position: absolute;
  inset: 18px;
  border-radius: 50%;
  border: 1px solid rgba(37, 99, 235, 0.22);
}

.magnifier-large::after {
  content: "";
  position: absolute;
  right: -54px;
  bottom: -34px;
  width: 76px;
  height: 15px;
  border-radius: 999px;
  background: #1e3a8a;
  transform: rotate(45deg);
}

@keyframes magnifierSearch {
  0%,
  100% {
    transform: translate(0, 0) rotate(-3deg);
  }

  50% {
    transform: translate(12px, 10px) rotate(4deg);
  }
}

.search-dots {
  position: absolute;
  left: 92px;
  bottom: 18px;
  display: flex;
  gap: 10px;
}

.search-dots span {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #2563eb;
  animation: dotBounce 1.1s ease-in-out infinite;
}

.search-dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.search-dots span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes dotBounce {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.45;
  }

  50% {
    transform: translateY(-8px);
    opacity: 1;
  }
}

/* =========================
   MAP PIN + CONES
========================= */

.map-pin {
  position: absolute;
  right: 92px;
  bottom: 168px;
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  border-radius: 50% 50% 50% 4px;
  background: #dc2626;
  color: #ffffff;
  font-weight: 900;
  transform: rotate(-45deg);
  box-shadow: 0 16px 34px rgba(220, 38, 38, 0.22);
  animation: pinBounce 1.8s ease-in-out infinite;
}

.map-pin span {
  transform: rotate(45deg);
}

@keyframes pinBounce {
  0%,
  100% {
    transform: rotate(-45deg) translateY(0);
  }

  50% {
    transform: rotate(-45deg) translateY(-8px);
  }
}

.traffic-cone {
  position: absolute;
  bottom: 32px;
  width: 48px;
  height: 64px;
}

.cone-one {
  right: 114px;
}

.cone-two {
  right: 54px;
  transform: scale(0.82);
  opacity: 0.82;
}

.traffic-cone::before {
  content: "";
  position: absolute;
  left: 9px;
  bottom: 8px;
  width: 30px;
  height: 50px;
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  background: #f97316;
}

.traffic-cone::after {
  content: "";
  position: absolute;
  left: 2px;
  bottom: 0;
  width: 44px;
  height: 10px;
  border-radius: 3px;
  background: #334155;
}

.traffic-cone span {
  position: absolute;
  left: 15px;
  bottom: 25px;
  width: 18px;
  height: 6px;
  background: #ffffff;
  z-index: 2;
}

/* =========================
   WARNING CARD
========================= */

.warning-card {
  position: absolute;
  left: 34px;
  bottom: 34px;
  width: min(340px, calc(100% - 68px));
  padding: 20px;
  background: #ffffff;
  border-left: 5px solid #dc2626;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.12);
}

.warning-card span {
  color: #dc2626;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.16em;
}

.warning-card strong {
  display: block;
  margin-top: 8px;
  color: #0f172a;
  font-size: 18px;
}

.warning-card p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

/* =========================
   RESPONSIVE
========================= */

@media (max-width: 1080px) {
  .not-found-shell {
    grid-template-columns: 1fr;
    padding-top: 80px;
  }

  .animation-panel {
    min-height: 560px;
  }
}

@media (max-width: 680px) {
  .not-found-page {
    align-items: flex-start;
    padding: 32px 0;
  }

  .not-found-shell {
    width: min(100% - 28px, 1180px);
    gap: 36px;
    padding-top: 28px;
  }

  .brand {
    margin-bottom: 32px;
  }

  .brand img {
    width: 140px;
  }

  h1 {
    font-size: clamp(84px, 28vw, 130px);
  }

  h2 {
    font-size: clamp(34px, 11vw, 48px);
  }

  .actions {
    flex-direction: column;
  }

  .primary-btn,
  .secondary-btn {
    width: 100%;
  }

  .animation-panel {
    min-height: 500px;
  }

  .building-one {
    left: 12px;
    width: 100px;
    height: 230px;
  }

  .building-two {
    left: 118px;
    width: 130px;
    height: 340px;
  }

  .building-three {
    right: 12px;
    width: 110px;
    height: 260px;
  }

  .search-scene {
    left: 50%;
    top: 130px;
    width: 300px;
    height: 300px;
  }

  .blueprint-card {
    left: 6px;
    top: 84px;
    width: 215px;
    padding: 18px;
  }

  .blueprint-card strong {
    font-size: 19px;
  }

  .magnifier-large {
    right: 0;
    top: 40px;
    width: 96px;
    height: 96px;
    border-width: 9px;
  }

  .magnifier-large::after {
    right: -42px;
    bottom: -27px;
    width: 58px;
    height: 12px;
  }

  .map-pin {
    right: 36px;
    bottom: 142px;
    width: 42px;
    height: 42px;
  }

  .traffic-cone {
    bottom: 24px;
  }

  .cone-one {
    right: 72px;
  }

  .cone-two {
    right: 24px;
  }

  .sun {
    width: 62px;
    height: 62px;
    top: 44px;
    right: 34px;
  }

  .warning-card {
    left: 18px;
    bottom: 18px;
    width: calc(100% - 36px);
  }
}
</style>